/**
 * Feedback Service Hook
 * Manages feedback submission, storage, and retrieval with real API integration
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  Feedback, 
  FeedbackWithStatus, 
  FeedbackLocalStorage,
  FeedbackAnalytics,
  FeedbackFilter,
  FeedbackSort
} from '../types/feedback';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const STORAGE_KEY = 'clear_feedback_queue';
const SUBMISSION_RETRY_DELAY = 5000; // 5 seconds
const MAX_RETRY_ATTEMPTS = 3;

interface FeedbackServiceState {
  isSubmitting: boolean;
  submissionError: string | null;
  pendingCount: number;
  isLoading: boolean;
}

interface FeedbackServiceActions {
  submitFeedback: (feedback: Feedback) => Promise<void>;
  getFeedback: (filters?: FeedbackFilter, sort?: FeedbackSort) => Promise<FeedbackWithStatus[]>;
  getAnalytics: () => Promise<FeedbackAnalytics>;
  exportFeedback: (format: 'csv' | 'json', filters?: FeedbackFilter) => Promise<void>;
  clearLocalQueue: () => void;
  syncPendingFeedback: () => Promise<void>;
  deleteFeedback: (feedbackId: string) => Promise<void>;
  updateFeedbackStatus: (feedbackId: string, status: string) => Promise<void>;
  archiveFeedback: (feedbackId: string) => Promise<void>;
}

export function useFeedbackService(): FeedbackServiceState & FeedbackServiceActions {
  const [state, setState] = useState<FeedbackServiceState>({
    isSubmitting: false,
    submissionError: null,
    pendingCount: 0,
    isLoading: false
  });

  // Initialize and load local queue
  useEffect(() => {
    loadLocalQueue();
    startAutoSync();
  }, []);

  const loadLocalQueue = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data: FeedbackLocalStorage = JSON.parse(stored);
        setState(prev => ({
          ...prev,
          pendingCount: data.submissionQueue?.length || 0
        }));
      }
    } catch (error) {
      console.error('Failed to load local queue:', error);
    }
  }, []);

  const getStorageData = useCallback((): FeedbackLocalStorage => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        console.log('Raw localStorage data:', data);
        
        // Validate and clean up submission queue
        if (data.submissionQueue) {
          data.submissionQueue.forEach((item: any, index: number) => {
            console.log(`Validating submission queue item ${index}:`, item);
            if (!item.feedback || !item.feedback.type) {
              console.warn(`Invalid feedback item at index ${index}:`, item);
            }
          });
        }
        
        return data;
      }
    } catch (error) {
      console.error('Failed to parse stored queue:', error);
    }

    return {
      pendingFeedback: [],
      userPreferences: {
        widgetPosition: 'bottom-right',
        enableNotifications: true,
        minimizeAfterSubmission: false
      },
      submissionQueue: []
    };
  }, []);

  const saveStorageData = useCallback((data: FeedbackLocalStorage) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setState(prev => ({
        ...prev,
        pendingCount: data.submissionQueue.length
      }));
    } catch (error) {
      console.error('Failed to save queue data:', error);
    }
  }, []);

  const generateFeedbackId = useCallback((): string => {
    return `feedback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const submitFeedback = useCallback(async (feedback: Feedback) => {
    setState(prev => ({ ...prev, isSubmitting: true, submissionError: null }));

    try {
      // Convert frontend lowercase types to API uppercase types
      const frontendToApiTypeMapping: Record<string, string> = {
        'quick-reaction': 'QUICK_REACTION',
        'page-comment': 'PAGE_COMMENT',
        'element-feedback': 'ELEMENT_FEEDBACK',
        'flow-rating': 'FLOW_RATING',
        'bug-report': 'BUG_REPORT',
        'feature-request': 'FEATURE_REQUEST',
        'general': 'GENERAL'
      };

      // Prepare feedback data
      const completeFeedback: Feedback = {
        ...feedback,
        id: feedback.id || generateFeedbackId(),
        timestamp: feedback.timestamp || new Date(),
        sessionId: feedback.sessionId || generateSessionId(),
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      };

      // Prepare API payload with uppercase type
      const apiPayload = {
        ...completeFeedback,
        type: frontendToApiTypeMapping[completeFeedback.type] || completeFeedback.type.toUpperCase()
      };

      console.log('Submitting feedback to API:', apiPayload);
      console.log('Original type:', completeFeedback.type, '-> API type:', apiPayload.type);

      // Try to submit to API
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload)
      });

      if (response.ok) {
        const result = await response.json();
        setState(prev => ({ ...prev, isSubmitting: false }));
        showNotification('Feedback submitted successfully!', 'success');
        
        // Broadcast event for real-time dashboard updates
        window.dispatchEvent(new CustomEvent('feedbackSubmitted', {
          detail: { feedbackId: result.data?.feedbackId, type: completeFeedback.type }
        }));
        
        return;
      }

      // If API fails, add to retry queue
      throw new Error(`API returned ${response.status}`);
      
    } catch (error) {
      // Add to local queue for retry
      const data = getStorageData();
      const submissionItem = {
        feedback: {
          ...completeFeedback,
          type: completeFeedback.type
        } as Feedback,
        retryCount: 0,
        lastAttempt: new Date()
      };
      
      console.log('Adding feedback to submission queue:', submissionItem);
      console.log('Feedback type being stored:', submissionItem.feedback.type);
      console.log('Complete feedback object:', submissionItem.feedback);
      data.submissionQueue.push(submissionItem);
      saveStorageData(data);
      
      console.log('Updated local storage data:', data);
      console.log('Total items in submission queue:', data.submissionQueue.length);
      
      setState(prev => ({ 
        ...prev, 
        isSubmitting: false,
        submissionError: error instanceof Error ? error.message : 'Unknown error'
      }));
      
      showNotification('Feedback saved. Will retry when connection is available.', 'info');
    }
  }, [generateFeedbackId, getStorageData, saveStorageData]);

  const getFeedback = useCallback(async (filters?: FeedbackFilter, sort?: FeedbackSort): Promise<FeedbackWithStatus[]> => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Build query params
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.type) params.append('type', Array.isArray(filters.type) ? filters.type.join(',') : filters.type);
        if (filters.status) params.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
        if (filters.priority) params.append('priority', Array.isArray(filters.priority) ? filters.priority.join(',') : filters.priority);
        if (filters.pageUrl) params.append('pageUrl', filters.pageUrl);
        if (filters.dateRange) {
          params.append('startDate', filters.dateRange.start.toISOString());
          params.append('endDate', filters.dateRange.end.toISOString());
        }
      }
      
      if (sort) {
        params.append('sortBy', sort.field);
        params.append('sortOrder', sort.direction);
      }

      const response = await fetch(`${API_BASE_URL}/feedback?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch feedback: ${response.status}`);
      }

      const result = await response.json();
      setState(prev => ({ ...prev, isLoading: false }));
      
      console.log('API response result:', result);
      
      // Extract feedback array from response
      if (result && result.data && Array.isArray(result.data.feedback)) {
        console.log('Processing API feedback items:', result.data.feedback);
        
        // Ensure each feedback item has proper type preservation and normalization
        const processedFeedback = result.data.feedback.map((item: any, index: number) => {
          console.log(`API feedback item ${index}:`, item);
          console.log(`API feedback type (raw): ${item?.type}`);
          
          // Convert uppercase API types to lowercase frontend types
          const typeMapping: Record<string, string> = {
            'QUICK_REACTION': 'quick-reaction',
            'PAGE_COMMENT': 'page-comment',
            'ELEMENT_FEEDBACK': 'element-feedback',
            'FLOW_RATING': 'flow-rating',
            'BUG_REPORT': 'bug-report',
            'FEATURE_REQUEST': 'feature-request',
            'GENERAL': 'general'
          };
          
          const normalizedType = typeMapping[item?.type] || item?.type?.toLowerCase() || 'general';
          
          // Ensure type field is properly preserved and normalized
          const feedbackWithType = {
            ...item,
            type: normalizedType
          };
          
          console.log(`Processed API feedback type: ${feedbackWithType.type}`);
          return feedbackWithType;
        });
        
        console.log('Final processed API feedback:', processedFeedback);
        return processedFeedback;
      }
      
      // Return empty array if structure is unexpected
      return [];
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setState(prev => ({ ...prev, isLoading: false }));
      
      // Fallback to local storage when API is unavailable
      try {
        const localData = getStorageData();
        const localFeedback: FeedbackWithStatus[] = [];
        
        console.log('Local storage data:', localData);
        console.log('Submission queue length:', localData.submissionQueue?.length || 0);
        console.log('Pending feedback length:', localData.pendingFeedback?.length || 0);
        
        // Add submitted feedback from retry queue
        if (localData.submissionQueue && Array.isArray(localData.submissionQueue)) {
          localData.submissionQueue.forEach((item, index) => {
            console.log(`Processing submission queue item ${index}:`, item);
            console.log(`Original feedback type: ${item.feedback?.type}`);
            if (item.feedback && item.feedback.type) {
              const feedbackWithStatus: FeedbackWithStatus = {
                ...item.feedback,
                type: item.feedback.type, // Explicitly preserve type
                status: 'new',
                priority: 'medium'
              };
              console.log(`Final feedback type: ${feedbackWithStatus.type}`);
              localFeedback.push(feedbackWithStatus);
            } else {
              console.warn(`Skipping malformed submission queue item ${index}:`, item);
            }
          });
        }
        
        // Add any other pending feedback
        if (localData.pendingFeedback && Array.isArray(localData.pendingFeedback)) {
          localData.pendingFeedback.forEach((feedback, index) => {
            console.log(`Processing pending feedback item ${index}:`, feedback);
            console.log(`Pending feedback type: ${feedback?.type}`);
            if (feedback && feedback.type) {
              const feedbackWithStatus: FeedbackWithStatus = {
                ...feedback,
                type: feedback.type, // Explicitly preserve type
                status: 'new', 
                priority: 'medium'
              };
              console.log(`Final pending feedback type: ${feedbackWithStatus.type}`);
              localFeedback.push(feedbackWithStatus);
            } else {
              console.warn(`Skipping malformed feedback item ${index}:`, feedback);
            }
          });
        }
        
        console.log(`Returning ${localFeedback.length} feedback items from local storage:`, localFeedback);
        return localFeedback;
      } catch (localError) {
        console.error('Error reading local feedback:', localError);
        return [];
      }
    }
  }, []);

  const getAnalytics = useCallback(async (): Promise<FeedbackAnalytics> => {
    try {
      const response = await fetch(`${API_BASE_URL}/feedback/analytics`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      
      // Return default analytics on error
      return {
        totalCount: 0,
        byType: {
          'quick-reaction': 0,
          'page-comment': 0,
          'element-feedback': 0,
          'flow-rating': 0,
          'bug-report': 0,
          'feature-request': 0,
          'general': 0
        },
        byStatus: {
          'new': 0,
          'reviewing': 0,
          'in-progress': 0,
          'resolved': 0,
          'closed': 0,
          'archived': 0
        },
        byPriority: {
          'low': 0,
          'medium': 0,
          'high': 0,
          'urgent': 0
        },
        topPages: [],
        trends: {
          daily: [],
          weekly: []
        }
      };
    }
  }, []);

  const exportFeedback = useCallback(async (format: 'csv' | 'json', filters?: FeedbackFilter) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.type) params.append('type', Array.isArray(filters.type) ? filters.type.join(',') : filters.type);
        if (filters.status) params.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
        if (filters.pageUrl) params.append('pageUrl', filters.pageUrl);
      }

      const response = await fetch(`${API_BASE_URL}/feedback/export?format=${format}&${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`Failed to export feedback: ${response.status}`);
      }

      // Get the filename from headers or use default
      const disposition = response.headers.get('Content-Disposition');
      const filename = disposition?.match(/filename="(.+)"/)?.[1] || `feedback_${Date.now()}.${format}`;
      
      // Download the file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showNotification(`Feedback exported as ${format.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Error exporting feedback:', error);
      showNotification('Failed to export feedback', 'error');
    }
  }, []);

  const syncPendingFeedback = useCallback(async () => {
    const data = getStorageData();
    const queue = [...data.submissionQueue];

    for (let i = queue.length - 1; i >= 0; i--) {
      const item = queue[i];
      
      if (item.retryCount >= MAX_RETRY_ATTEMPTS) {
        // Remove failed items after max retries
        queue.splice(i, 1);
        continue;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/feedback`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(item.feedback)
        });

        if (response.ok) {
          // Remove from queue on success
          queue.splice(i, 1);
          showNotification(`Feedback "${item.feedback.id}" submitted successfully!`, 'success');
          
          // Broadcast event for synced feedback too
          window.dispatchEvent(new CustomEvent('feedbackSubmitted', {
            detail: { feedbackId: item.feedback.id, type: item.feedback.type }
          }));
        } else {
          // Update retry count
          item.retryCount++;
          item.lastAttempt = new Date();
        }
      } catch (error) {
        // Update retry count on network error
        item.retryCount++;
        item.lastAttempt = new Date();
      }
    }

    data.submissionQueue = queue;
    saveStorageData(data);
  }, [getStorageData, saveStorageData]);

  const startAutoSync = useCallback(() => {
    const interval = setInterval(() => {
      if (navigator.onLine) {
        syncPendingFeedback();
      }
    }, SUBMISSION_RETRY_DELAY);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [syncPendingFeedback]);

  const clearLocalQueue = useCallback(() => {
    const data = getStorageData();
    data.submissionQueue = [];
    saveStorageData(data);
    showNotification('Local queue cleared', 'info');
  }, [getStorageData, saveStorageData]);

  const deleteFeedback = useCallback(async (feedbackId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/feedback?id=${feedbackId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete feedback: ${response.status}`);
      }

      showNotification('Feedback deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting feedback:', error);
      showNotification('Failed to delete feedback', 'error');
      throw error;
    }
  }, []);

  const updateFeedbackStatus = useCallback(async (feedbackId: string, status: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/feedback?id=${feedbackId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: status.toUpperCase() })
      });

      if (!response.ok) {
        throw new Error(`Failed to update feedback status: ${response.status}`);
      }

      showNotification('Feedback status updated', 'success');
    } catch (error) {
      console.error('Error updating feedback status:', error);
      showNotification('Failed to update status', 'error');
      throw error;
    }
  }, []);

  const archiveFeedback = useCallback(async (feedbackId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/feedback?id=${feedbackId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'ARCHIVED' })
      });

      if (!response.ok) {
        throw new Error(`Failed to archive feedback: ${response.status}`);
      }

      showNotification('Feedback archived successfully', 'success');
    } catch (error) {
      console.error('Error archiving feedback:', error);
      showNotification('Failed to archive feedback', 'error');
      throw error;
    }
  }, []);

  return {
    ...state,
    submitFeedback,
    getFeedback,
    getAnalytics,
    exportFeedback,
    clearLocalQueue,
    syncPendingFeedback,
    deleteFeedback,
    updateFeedbackStatus,
    archiveFeedback
  };
}

// Helper functions
function showNotification(message: string, type: 'success' | 'error' | 'info') {
  // Simple console log - in production, integrate with your notification system
  console.log(`[${type.toUpperCase()}] ${message}`);
  
  // Browser notification if permitted
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('CLEAR Feedback', { 
      body: message,
      icon: '/favicon.ico'
    });
  }
}

function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}